import Link from 'next/link';
import {
  CSSProperties,
  KeyboardEvent,
  ReactNode,
  useRef,
  useState,
} from 'react';
import useSWR from 'swr';
import { AccountIcon } from 'components/account/AccountIcons';
import CommentSection from 'components/community/comment/CommentSection';
import { commentsHandler } from 'lib/comments';
import { getCommunityPath } from 'lib/community';
import { jsonFetcherWithHandler as fetcher } from 'lib/swr';
import { CommunityShotItem, ShotRemix } from 'types/community';
import CommunityShotNotReady from './CommunityShotNotReady';
import RemixesSection from './RemixesSection';
import RevisionSelect from './RevisionSelect';
import ShotMetadata from './ShotMetadata';
import styles from './ShotPageRefresh.module.css';

type DiscussionTab = 'comments' | 'remixes';

type Props = {
  shotItem: CommunityShotItem;
  remixes: ShotRemix[];
  preview: ReactNode;
  previewStyle: CSSProperties;
  fullscreenButton: ReactNode;
  controls: ReactNode;
  disableReactionsTray: boolean;
};

export default function ShotPageRefresh({
  shotItem,
  remixes,
  preview,
  previewStyle,
  fullscreenButton,
  controls,
  disableReactionsTray,
}: Props) {
  const [tab, setTab] = useState<DiscussionTab>('comments');
  const discussionRef = useRef<HTMLElement>(null);
  const tabRefs = useRef<Record<DiscussionTab, HTMLButtonElement | null>>({
    comments: null,
    remixes: null,
  });
  const { data: comments } = useSWR(
    `/api/community-posts/${shotItem.communityPostId}/comments`,
    fetcher(commentsHandler)
  );
  const tabs: { key: DiscussionTab; label: string; count: number }[] = [
    { key: 'comments', label: 'Comments', count: comments?.length ?? 0 },
    { key: 'remixes', label: 'Remixes', count: shotItem.publishedRemixCount },
  ];

  function onTabKeyDown(event: KeyboardEvent) {
    const index = tabs.findIndex(({ key }) => key === tab);
    const next = {
      ArrowRight: index + 1,
      ArrowLeft: index - 1,
      Home: 0,
      End: tabs.length - 1,
    }[event.key];
    if (next === undefined) {
      return;
    }
    event.preventDefault();
    const { key } = tabs[(next + tabs.length) % tabs.length];
    setTab(key);
    tabRefs.current[key]?.focus();
  }

  function onCommentsClick() {
    setTab('comments');
    discussionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }

  return (
    <div className={styles.Page}>
      <Link href={getCommunityPath('featured')} className={styles.Back}>
        <AccountIcon name="chevronLeft" size={14} strokeWidth={2} />
        Featured
      </Link>

      <div className={styles.Columns}>
        <div className={styles.Preview}>
          <div className={styles.Stage} style={previewStyle}>
            {preview}
          </div>
          {!shotItem.isReadyForViewing && <CommunityShotNotReady />}
          <span className={styles.LiveLabel}>Live preview</span>
          <div className={styles.PreviewBar}>
            <div className={styles.Viewing}>
              Viewing
              <RevisionSelect shotItem={shotItem} />
            </div>
            {fullscreenButton}
          </div>
        </div>

        <aside className={styles.Aside}>
          <ShotMetadata
            shotItem={shotItem}
            onCommentsClick={onCommentsClick}
            disableReactionsTray={disableReactionsTray}
          />
          {shotItem.isAnimationStateMachine && (
            <section className={styles.Play}>
              <div className={styles.PlayHeader}>
                <h2>Play with it</h2>
                <span>Live</span>
              </div>
              <div className={styles.PlayBody}>{controls}</div>
            </section>
          )}
        </aside>

        <section ref={discussionRef} className={styles.Discussion}>
          <div
            role="tablist"
            aria-label="Discussion"
            className={styles.Tabs}
            onKeyDown={onTabKeyDown}
          >
            {tabs.map(({ key, label, count }) => (
              <button
                key={key}
                ref={(button) => {
                  tabRefs.current[key] = button;
                }}
                type="button"
                role="tab"
                id={`discussion-tab-${key}`}
                aria-selected={tab === key}
                aria-controls="discussion-panel"
                tabIndex={tab === key ? 0 : -1}
                className={styles.Tab}
                onClick={() => setTab(key)}
              >
                {label}
                {count > 0 && <span className={styles.TabCount}>{count}</span>}
              </button>
            ))}
          </div>
          <div
            role="tabpanel"
            id="discussion-panel"
            aria-labelledby={`discussion-tab-${tab}`}
            className={styles.TabPanel}
          >
            {tab === 'comments' ? (
              <CommentSection shotItem={shotItem} />
            ) : (
              <RemixesSection shotItem={shotItem} remixes={remixes} />
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
